import * as React from "react";

import { Alert, Badge, Button, notify } from "Components/ui";
import { WhatsApp } from "Components/ui/icons";
import { formatDateTime } from "utils/format";
import type { ProfileData } from "../data";
import { displayPhone } from "../phone";
import PhoneFlow, { type PhoneFlowStart } from "./PhoneFlow";
import Section from "./Section";

// Matches the server's code policy (utils/otpService.js).
const CODE_TTL_SECONDS = 10 * 60;
const RESEND_AFTER_SECONDS = 30;

/** Seconds until a code issued to expire at `expiresAt` may be resent. */
const resendWaitFor = (expiresAt: string): number => {
  const issuedAt = new Date(expiresAt).getTime() - CODE_TTL_SECONDS * 1000;
  return Math.max(0, Math.ceil((issuedAt + RESEND_AFTER_SECONDS * 1000 - Date.now()) / 1000));
};

/** The account's WhatsApp number, where sign-in and password-reset codes are sent. */
const WhatsAppSection: React.FC<{ profile: ProfileData }> = ({ profile }) => {
  const { user, requestPhoneCode } = profile;
  const [dialog, setDialog] = React.useState<PhoneFlowStart | null>(null);
  const [sending, setSending] = React.useState(false);

  if (!user) {
    return null;
  }
  const number = user.phoneNumber || null;
  const verified = Boolean(number && user.phoneVerified);
  const pending = user.pendingPhone || null;

  const verifyCurrent = async () => {
    setSending(true);
    try {
      const res = await requestPhoneCode();
      setDialog({
        step: "code",
        target: number || "",
        change: false,
        resendIn: res.resendAfterSeconds,
      });
    } catch (err) {
      notify.error(err, { fallback: "The code couldn't be sent. Try again shortly." });
    } finally {
      setSending(false);
    }
  };

  let status: React.ReactNode;
  if (!number) {
    status = <Badge>Not set</Badge>;
  } else if (verified) {
    status = (
      <Badge tone="success" dot>
        Verified
      </Badge>
    );
  } else {
    status = (
      <Badge tone="warning" dot>
        Not verified
      </Badge>
    );
  }

  return (
    <Section
      title="WhatsApp number"
      description="Sign-in and password-reset codes are sent to this number."
      aside={status}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-success-subtle text-success-text [&_svg]:h-5 [&_svg]:w-5">
          <WhatsApp aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-medium tabular-nums text-foreground">
            {number ? displayPhone(number) : "No number yet"}
          </p>
          <p className="text-sm text-muted-foreground">
            {!number
              ? "Add one, or you can't sign in again once this session ends."
              : verified
              ? "Confirmed with a WhatsApp code."
              : "Set by an admin. Verify it to confirm it's yours."}
          </p>
        </div>
        {!dialog && (
          <div className="flex flex-wrap gap-2">
            {number && !verified && (
              <Button variant="secondary" size="sm" onClick={verifyCurrent} loading={sending}>
                Verify number
              </Button>
            )}
            <Button
              variant={number ? "secondary" : "primary"}
              size="sm"
              onClick={() => setDialog({ step: "number" })}
              disabled={sending}
            >
              {number ? "Change number" : "Add number"}
            </Button>
          </div>
        )}
      </div>
      {pending && !dialog && (
        <Alert
          tone="info"
          title={pending.change ? "Waiting for your code" : "Verification code sent"}
          action={
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                setDialog({
                  step: "code",
                  target: pending.phoneNumber,
                  change: pending.change,
                  resendIn: resendWaitFor(pending.expiresAt),
                })
              }
            >
              Enter code
            </Button>
          }
        >
          {pending.change
            ? `${displayPhone(
                pending.phoneNumber
              )} becomes your number once you enter the code sent to it. `
            : "Enter the code sent to this number to verify it. "}
          The code expires at {formatDateTime(pending.expiresAt)}.
        </Alert>
      )}

      {dialog && <PhoneFlow start={dialog} onClose={() => setDialog(null)} profile={profile} />}
    </Section>
  );
};

export default WhatsAppSection;
